import React, { useState, useRef, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Paperclip, X, Loader2, ImagePlus } from "lucide-react";
import { submitCommissionApi } from "../api";
import SEO from "../components/SEO";
import { WhatsAppIcon, waUrl } from "../components/WhatsAppFab";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

const SUBJECTS = ["Custom commission", "General enquiry", "Wholesale", "Press"];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Custom commission",
    budget: "",
    timeline: "",
    dimensions: "",
    message: "",
  });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isCommission = form.subject === "Custom commission";

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const merged = [...files];
    for (const f of incoming) {
      if (merged.length >= MAX_FILES) {
        toast.error(`You can attach up to ${MAX_FILES} photos.`);
        break;
      }
      if (!ALLOWED_MIME.includes(f.type)) {
        toast.error(`${f.name}: unsupported file type (JPG, PNG, WebP, HEIC only).`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: exceeds 10 MB.`);
        continue;
      }
      merged.push(f);
    }
    setFiles(merged);
    // Revoke old blob URLs before creating new ones
    previews.forEach((p) => URL.revokeObjectURL(p.url));
    setPreviews(merged.map((f) => ({ name: f.name, url: URL.createObjectURL(f), size: f.size })));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    URL.revokeObjectURL(previews[idx]?.url);
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    previews.forEach((p, i) => { if (i !== idx) URL.revokeObjectURL(p.url); });
    setPreviews(next.map((f) => ({ name: f.name, url: URL.createObjectURL(f), size: f.size })));
  };

  // Clean up all preview URLs on unmount
  useEffect(() => () => { previews.forEach((p) => URL.revokeObjectURL(p.url)); }, [previews]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please share your name, email and a short description (5+ characters).");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone || "");
      fd.append("category", form.subject || "");
      fd.append("budget", form.budget || "");
      fd.append("timeline", form.timeline || "");
      fd.append("dimensions", form.dimensions || "");
      fd.append("message", form.message);
      files.forEach((f) => fd.append("files", f, f.name));
      await submitCommissionApi(fd);
      toast.success("Thank you — the studio will reply within 48 hours.");
      setForm({ name: "", email: "", phone: "", subject: "Custom commission", budget: "", timeline: "", dimensions: "", message: "" });
      setFiles([]);
      setPreviews([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry. Please email venus3dcreations@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "h-12 rounded-none bg-transparent border-[var(--sand)] focus-visible:ring-[var(--copper)]";
  const labelCls = "text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60 mb-2 block";

  return (
    <main className="bg-[var(--cream)]">
      <SEO
        title="Contact the studio"
        description="Commissions, press, wholesale or questions — the Venus 3D Creations studio replies to every message personally within 48 hours. WhatsApp, email or phone."
      />
      <section className="bg-[var(--ivory)] py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--copper)] mb-6">— Get in touch</p>
          <h1 className="font-serif-display text-5xl lg:text-7xl leading-[1.05] text-[var(--espresso)]">
            Talk to the <em className="italic text-[var(--copper)]">studio</em>.
          </h1>
          <p className="mt-6 text-lg text-[var(--espresso)]/70 max-w-2xl">
            Commissions, press, wholesale, or just to say hello. We answer every message personally within 48 hours.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-8">
            {[
              { Icon: MapPin, t: "Studio", v: "Ahmedabad, India — by appointment only" },
              { Icon: Mail, t: "Email", v: "venus3dcreations@gmail.com" },
              { Icon: Phone, t: "Phone", v: "+91 88494 40828" },
              { Icon: Clock, t: "Hours", v: "Mon–Sat  10:00–19:00 IST" },
            ].map(({ Icon, t, v }) => (
              <div key={t} className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[var(--sand)] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[var(--copper)]" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-[var(--copper)]">{t}</p>
                  <p className="text-[var(--espresso)]/80 mt-1">{v}</p>
                </div>
              </div>
            ))}

            <a
              href={waUrl("Hi Venus 3D Creations! I'd like to chat about")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366]/20 transition-colors">
                <WhatsAppIcon className="w-5 h-5 text-[#128C7E]" />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[var(--copper)]">WhatsApp</p>
                <p className="text-[var(--espresso)]/80 mt-1 group-hover:text-[var(--copper)] transition-colors">Chat with the studio →</p>
              </div>
            </a>

            <div className="pt-8 border-t border-[var(--sand)] text-sm text-[var(--espresso)]/70">
              <p className="font-serif-display text-2xl text-[var(--espresso)] mb-3">Commissioning a piece?</p>
              <p>Share a rough idea, reference photos and your intended space. We'll send back a concept sketch, quote and timeline within two working days.</p>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="commission-form" className="lg:col-span-8 bg-[var(--ivory)] p-8 lg:p-12 space-y-6">
            <div>
              <label className={labelCls}>How can we help?</label>
              <div className="flex flex-wrap gap-2">
                {SUBJECTS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setForm({ ...form, subject: s })}
                    data-testid={`subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 h-10 text-xs tracking-[0.14em] uppercase border transition-colors ${
                      form.subject === s
                        ? "bg-[var(--espresso)] text-white border-[var(--espresso)]"
                        : "border-[var(--sand)] hover:border-[var(--copper)]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Name*</label>
                <Input value={form.name} onChange={upd("name")} data-testid="commission-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email*</label>
                <Input type="email" value={form.email} onChange={upd("email")} data-testid="commission-email" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone (optional)</label>
                <Input value={form.phone} onChange={upd("phone")} className={inputCls} />
              </div>
              {isCommission && (
                <div>
                  <label className={labelCls}>Approx. budget (optional)</label>
                  <Input value={form.budget} onChange={upd("budget")} placeholder="e.g. ₹15,000–₹25,000" className={inputCls} />
                </div>
              )}
              {isCommission && (
                <>
                  <div>
                    <label className={labelCls}>Timeline (optional)</label>
                    <Input value={form.timeline} onChange={upd("timeline")} placeholder="e.g. by mid-October" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Dimensions / space (optional)</label>
                    <Input value={form.dimensions} onChange={upd("dimensions")} placeholder='e.g. living room, ~24" tall' className={inputCls} />
                  </div>
                </>
              )}
            </div>

            <div>
              <label className={labelCls}>{isCommission ? "Describe the piece*" : "Message*"}</label>
              <Textarea
                value={form.message}
                onChange={upd("message")}
                rows={6}
                data-testid="commission-message"
                className="rounded-none bg-transparent border-[var(--sand)] focus-visible:ring-[var(--copper)]"
                placeholder={isCommission ? "Style, mood, material feel, colour palette, where it will live…" : "How can we help?"}
              />
            </div>

            <div>
              <label className={labelCls}>Reference photos (optional · up to 5, 10 MB each)</label>
              <div className="flex flex-wrap gap-3">
                {previews.map((p, i) => (
                  <div key={p.url} className="relative w-24 h-24 bg-[var(--sand)] overflow-hidden group">
                    <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove ${p.name}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {files.length < MAX_FILES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="commission-attach-btn"
                    className="w-24 h-24 border border-dashed border-[var(--copper)]/60 text-[var(--copper)] flex flex-col items-center justify-center gap-1 hover:bg-[var(--copper)]/5 transition-colors"
                  >
                    <ImagePlus className="w-5 h-5" />
                    <span className="text-[10px] tracking-widest uppercase">Add photo</span>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_MIME.join(",")}
                multiple
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
                data-testid="commission-file-input"
              />
              {files.length > 0 && (
                <p className="mt-2 text-[10px] tracking-[0.15em] uppercase text-[var(--espresso)]/50 flex items-center gap-1.5">
                  <Paperclip className="w-3 h-3" /> {files.length} of {MAX_FILES} attached
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="commission-submit-btn"
              className="h-12 px-10 vc-btn-copper text-xs tracking-[0.2em] uppercase disabled:opacity-60 flex items-center gap-2"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : "Send enquiry"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
